import React, { useEffect, useRef, useState } from 'react';
import {
    Animated,
    Modal,
    Platform,
    Pressable,
    ScrollView,
    StyleSheet,
    Text,
    TouchableOpacity,
    View,
} from 'react-native';
import { FontAwesome6 } from '@expo/vector-icons';
import colors from '../../lib/colors';
import { useFontLoading } from '../../lib/FontContext';
import { interSemiBold } from '../../lib/fonts';
import { FeedPaper } from '../../lib/hooks/useProjectFeed';
import { DownvoteReason, FeedbackVote } from '../../lib/projectPaperFeedback';
import ProjectFeedDetailContent from './ProjectFeedDetailContent';
import { Trans } from '@lingui/react/macro';

interface ProjectFeedDetailSheetProps {
    visible: boolean;
    item: FeedPaper | null;
    paperLinkHref?: string;
    feedbackPending: boolean;
    onClose: () => void;
    onSetFeedback: (vote: FeedbackVote, reason?: DownvoteReason) => Promise<void>;
    onClearFeedback: () => Promise<void>;
}

const SHEET_ANIMATION_MS = 220;

export default function ProjectFeedDetailSheet({
    visible,
    item,
    paperLinkHref,
    feedbackPending,
    onClose,
    onSetFeedback,
    onClearFeedback,
}: ProjectFeedDetailSheetProps) {
    const { fontsLoaded } = useFontLoading();
    const [renderModal, setRenderModal] = useState(visible);
    const overlayOpacity = useRef(new Animated.Value(0)).current;
    const translateProgress = useRef(new Animated.Value(1)).current;

    useEffect(() => {
        if (visible) {
            setRenderModal(true);
            overlayOpacity.setValue(0);
            translateProgress.setValue(1);
            Animated.parallel([
                Animated.timing(overlayOpacity, {
                    toValue: 1,
                    duration: SHEET_ANIMATION_MS,
                    useNativeDriver: true,
                }),
                Animated.timing(translateProgress, {
                    toValue: 0,
                    duration: SHEET_ANIMATION_MS,
                    useNativeDriver: true,
                }),
            ]).start();
            return;
        }

        Animated.parallel([
            Animated.timing(overlayOpacity, {
                toValue: 0,
                duration: SHEET_ANIMATION_MS,
                useNativeDriver: true,
            }),
            Animated.timing(translateProgress, {
                toValue: 1,
                duration: SHEET_ANIMATION_MS,
                useNativeDriver: true,
            }),
        ]).start(({ finished }) => {
            if (finished) {
                setRenderModal(false);
            }
        });
    }, [overlayOpacity, translateProgress, visible]);

    if (!renderModal || !item || !paperLinkHref) {
        return null;
    }

    const translateY = translateProgress.interpolate({
        inputRange: [0, 1],
        outputRange: [0, 56],
    });

    return (
        <Modal
            transparent
            visible={renderModal}
            animationType="none"
            onRequestClose={onClose}
        >
            <View style={styles.modalRoot} testID="project-feed-detail-sheet">
                <Pressable style={StyleSheet.absoluteFill} onPress={onClose}>
                    <Animated.View style={[styles.overlay, { opacity: overlayOpacity }]} />
                </Pressable>

                <Animated.View style={[styles.sheet, { transform: [{ translateY }] }]}>
                    <View style={styles.sheetHeader}>
                        <View style={styles.handle} />
                        <Text style={[styles.sheetTitle, fontsLoaded && interSemiBold]}><Trans id="feed.projectfeeddetailsheet.paperDetails" comment="UI copy in components/feed/ProjectFeedDetailSheet.tsx">
                            Paper details
                        </Trans></Text>
                        <TouchableOpacity
                            style={styles.closeButton}
                            onPress={onClose}
                            activeOpacity={0.75}
                            testID="project-feed-detail-sheet-close"
                        >
                            <FontAwesome6 name="xmark" size={14} color={colors.text700} />
                        </TouchableOpacity>
                    </View>

                    <ScrollView
                        style={styles.scrollView}
                        contentContainerStyle={styles.scrollContent}
                        showsVerticalScrollIndicator={false}
                    >
                        <ProjectFeedDetailContent
                            item={item}
                            paperLinkHref={paperLinkHref}
                            feedbackPending={feedbackPending}
                            onSetFeedback={onSetFeedback}
                            onClearFeedback={onClearFeedback}
                        />
                    </ScrollView>
                </Animated.View>
            </View>
        </Modal>
    );
}

const styles = StyleSheet.create({
    modalRoot: {
        flex: 1,
        justifyContent: 'flex-end',
    },
    overlay: {
        ...StyleSheet.absoluteFill,
        backgroundColor: 'rgba(11, 16, 32, 0.28)',
    },
    sheet: {
        maxHeight: '90%',
        minHeight: '64%',
        borderTopLeftRadius: 22,
        borderTopRightRadius: 22,
        backgroundColor: colors.card,
        overflow: 'hidden',
        borderTopWidth: 1,
        borderLeftWidth: Platform.OS === 'web' ? 1 : 0,
        borderRightWidth: Platform.OS === 'web' ? 1 : 0,
        borderColor: colors.border,
    },
    sheetHeader: {
        alignItems: 'center',
        justifyContent: 'center',
        paddingTop: 10,
        paddingBottom: 14,
        paddingHorizontal: 20,
        borderBottomWidth: 1,
        borderBottomColor: '#edf0f4',
    },
    handle: {
        width: 42,
        height: 4,
        borderRadius: 999,
        backgroundColor: '#c8ced8',
        marginBottom: 10,
    },
    sheetTitle: {
        fontSize: 13,
        color: colors.text700,
    },
    closeButton: {
        position: 'absolute',
        top: 12,
        right: 16,
        width: 32,
        height: 32,
        borderRadius: 16,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f4f6fa',
        ...(Platform.OS === 'web' && {
            cursor: 'pointer',
        }),
    },
    scrollView: {
        flex: 1,
    },
    scrollContent: {
        paddingBottom: 32,
    },
});
